import type { Session } from "@supabase/supabase-js"
import { supabase } from "@fieldbook/common/supabase"
import { withTimeout } from "@/lib/withTimeout"

/**
 * Resolve the Supabase auth client's current session for a background network
 * request, with a hard timeout so a stalled token refresh eventually gives up
 * and lets the caller's retry strategy take over.
 *
 * "Live" means read from the auth client itself, which refreshes a token
 * that is close to expiry. It is still a local view: the server has not
 * confirmed the token and can reject it. Use isAccessTokenAccepted when a
 * caller needs that confirmation.
 *
 * Deliberately does not fall back to the query-cache mirror or the offline
 * identity shim (see cachedSession.ts): a stale cached session can produce an
 * unauthenticated or expired request even though the UI still considers the
 * user logged in.
 *
 * 30s (rather than failing fast) is intentional: field data collection
 * happens over spotty mobile reception where a token refresh can be slow
 * without being truly stuck, and the upload queues process items one at a
 * time, so giving a slow refresh room to complete avoids a needless retry.
 */
export async function getLiveSessionWithTimeout(
  timeoutMs = 30000,
): Promise<Session | null> {
  const network = supabase.auth
    .getSession()
    .then(({ data }) => data.session ?? null)
    .catch(() => null)

  return withTimeout(network, timeoutMs, () => null)
}

/**
 * True only if the auth server confirms `accessToken` is valid right now.
 *
 * Used to tell a genuine RLS denial (42501) apart from an auth problem that
 * surfaces as the same error. The caller must pass the exact token the failed
 * request was sent with (see createFieldbookSupabaseClientForToken): a JWT
 * that is valid now was valid when the earlier request was made, so a denial
 * under a confirmed token is a real policy decision. getSession() only checks
 * the token locally, so this asks the server via getUser(jwt), which also
 * skips the auth lock that a stalled refresh can hold.
 *
 * Any failure to confirm — network error, timeout, rejected token — returns
 * false, so callers treat "unsure" as an auth problem rather than a verdict
 * on the data.
 */
export async function isAccessTokenAccepted(
  accessToken: string,
  timeoutMs = 10000,
): Promise<boolean> {
  const verified = supabase.auth
    .getUser(accessToken)
    .then(({ data, error }) => !error && Boolean(data.user))
    .catch(() => false)

  return withTimeout(verified, timeoutMs, () => false)
}
